import React, { useState, useEffect } from "react";
import "./../../assets/css/TeamsSidebar.scss";
import { LuPanelLeftClose, LuPanelLeftOpen } from "react-icons/lu";
import useTitanApp from "../../hooks/useTitanApp";
import { Accordion, AccordionSummary, AccordionDetails } from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import Avatar from "react-initials-avatar";
import { motion } from "framer-motion";
import TeamMembers from "../Header/TeamMembers";
import { GoHomeFill } from "react-icons/go";
import { checkUserPermissions } from "../../utils/checkUserPermissions";
import {
  accessAddTeams,
  accessTitanverseTeamUpsert,
} from "../../data/constantStrings";
import AddTeam from "../Team/AddTeam";
import {
  setAddTeamImage,
  setAddTeamModal,
  setAddTeamPharmacyName,
  setAddTeamTitle,
  setTeamFormMode,
} from "../../dispatchers/teamDispatchers";
import { useFormik } from "formik";
import * as Yup from "yup";
import { getPharmaciesByCompanyId } from "../../dispatchers/pharmacyServiceDispatchers";
import { useNavigate } from "react-router";
import {
  setCompanyId,
  setCurrentCompanyId,
  setCurrentTeamId,
  setDasboardTeams,
  setHeaderTeamMembers,
} from "../../dispatchers/appDispatchers";
import TeamMembersSide from "../Header/TeamMembersSide";
import {
  setCurrentTeam,
  setSelectedTeamIndex,
} from "../../dispatchers/appDispatchers";
import PharmacySearch from "../../components/PharmacyFilter/PharmacySearch";
import { setCurrentPharmacy } from "../../dispatchers/appDispatchers";

export default function TeamsSidebar({
  triggerSmallView,
  setTriggerSmallView,
  handleClickedTeam,
}) {
  const { appState, teamState } = useTitanApp();
  const {
    darkMode,
    teams,
    companyId,
    currentTeamId,
    currentPharmacyId,
    currentPharmacy,
    headerTeamMembers = [],
    globalTab,
    globalTabText,
  } = appState || {};
  const { addTeamModal } = teamState || "";
  const navigate = useNavigate();
  const [expanded, setExpanded] = useState(false);
  const [pharmacyObj, setPharmacyObj] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchPharmacyText, setSearchPharmacyText] = useState("");
  const hasAddTeamPermission = checkUserPermissions(accessTitanverseTeamUpsert);

  const totalPharmacyCount = teams?.reduce(
    (acc, team) => acc + (team.pharmacies?.length || 0),
    0,
  );

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded(isExpanded ? panel : false);
  };

  const doesPharmacyMatch = (pharmacyName = "", search = "") =>
    pharmacyName.toLowerCase().includes(search.toLowerCase().trim());

  const teamsValidationSchema = Yup.object().shape({
    teamName: Yup.string().required("Please enter a team name"),
  });

  const teamsFormik = useFormik({
    initialValues: {
      teamName: "",
      displayName: "",
      teamPharmacyName: "",
    },
    validationSchema: teamsValidationSchema,
    onSubmit: (values) => {
      setAddTeamModal(false); // Close the dialog after submission
    },
  });

  const toggleTeamsDialog = (mode, initialValues = {}) => {
    setTeamFormMode(mode);

    if (mode === "add") {
      setAddTeamTitle("Global View");
      setAddTeamImage("");
      setAddTeamPharmacyName("");

      teamsFormik.setValues({
        teamName: "",
        displayName: "",
        teamPharmacyName: "",
      });

      setIsLoading(true);

      getPharmaciesByCompanyId(companyId)
        .then((data) => {
          if (!data) return;

          const pharmaciesObj = data.map((pharmacy) => ({
            teamPharmacyID: pharmacy.pharmacyID,
            pharmacyID: pharmacy.pharmacyID,
            teamPharmacyName: pharmacy.pharmacyName,
            teamPharmacyAddress: pharmacy.pharmacyAddress,
            teamPharmacyLongitude: pharmacy.longitude?.toString() || "",
            teamPharmacyLatitude: pharmacy.latitude?.toString() || "",
            clinicalKPI: pharmacy.clinicalKPI,
            financeKPI: pharmacy.financeKPI,
            operationsKPI: pharmacy.operationsKPI,
            efficiencyKPI: pharmacy.efficiencyKPI,
            pharmacyKPIs: pharmacy.pharmacyKPIs,
            pharmacyImage: pharmacy.pharmacyImage,
            isHelpRequired: pharmacy.isHelpRequired,
            patients: pharmacy.patients,
          }));

          setPharmacyObj(pharmaciesObj);
        })
        .catch((error) => {
          console.error("Error retrieving pharmacies", error);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      if (Object.keys(initialValues).length !== 0) {
        setAddTeamTitle(initialValues.teamName);
        setAddTeamPharmacyName(initialValues.teamPharmacyName);
      }

      teamsFormik.setValues({
        teamName: initialValues.teamName || "",
        displayName: initialValues.displayName || "",
        teamPharmacyName: initialValues.teamPharmacyName || "",
      });
    }

    setAddTeamModal((prev) => !prev);
  };

  const handleTeamPharmacyClick = (pharmacy, index) => {
    if (!pharmacy) return;

    setSelectedTeamIndex(index);
    const team = filteredTeams[index];

    if (team) {
      setCurrentCompanyId(team?.companyID);
      setCompanyId(team?.companyID);
      setDasboardTeams(team);
      setCurrentTeam(team);
      setCurrentPharmacy(pharmacy);
    }

    switch (globalTabText) {
      case "Services":
        navigate(`/pharmacy/services/${pharmacy.teamPharmacyID}`);
        break;

      case "Clinical Checks":
        navigate(`/pharmacy/clinicalchecks/${pharmacy.teamPharmacyID}`);
        break;

      case "PharmacyDashboard":
      case "Dashboard":
        navigate(`/pharmacy/dashboard/${pharmacy.teamPharmacyID}`);
        break;

      case "Calendar":
        navigate(`/pharmacy/schedule/${pharmacy.teamPharmacyID}`);
        break;

      case "Patients":
        navigate(`/pharmacy/patientlist/${pharmacy.teamPharmacyID}`);
        break;

      case "Approvals":
        navigate(`/pharmacy/prescriptionapproval/${pharmacy.teamPharmacyID}`);
        break;

      case "Documents":
        navigate(`/pharmacy/documents/${pharmacy.teamPharmacyID}`);
        break;

      case "Analytics":
        navigate(`/pharmacy/analytics/${pharmacy.teamPharmacyID}`);
        break;

      default:
        break;
    }
  };

  const filteredTeams = teams
    ?.map((team) => {
      const filteredPharmacies = searchPharmacyText
        ? team.pharmacies?.filter((pharmacy) =>
            doesPharmacyMatch(pharmacy.teamPharmacyName, searchPharmacyText),
          )
        : team.pharmacies;

      return {
        ...team,
        filteredPharmacies,
      };
    })
    .filter(
      (team) => team.filteredPharmacies?.length > 0 || !searchPharmacyText,
    );

  return (
    <motion.div
      style={{ width: !triggerSmallView ? "300px" : "fit-content" }}
      className="teams-sidebar-wrapper"
    >
      {triggerSmallView ? (
        <motion.div
          initial={{ y: -13, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 0.38 }}
          className="open-panel"
          onClick={() => setTriggerSmallView(false)}
        >
          <LuPanelLeftOpen color={darkMode ? "#fff" : "#0a0a0a"} />
        </motion.div>
      ) : (
        <motion.div
          initial={{ y: -13, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          transition={{ delay: 3 }}
          className="close-panel"
          onClick={() => setTriggerSmallView(true)}
        >
          <p>Close Panel</p>
          <LuPanelLeftClose color={darkMode ? "#fff" : "#0a0a0a"} />
        </motion.div>
      )}

      <div className="teams-list-container">
        {!triggerSmallView && totalPharmacyCount > 1 && (
          <div className="search_nfilter">
            <PharmacySearch
              searchValue={searchPharmacyText}
              setSearchValue={setSearchPharmacyText}
            />
          </div>
        )}

        {filteredTeams && filteredTeams.length > 0 ? (
          filteredTeams?.map((team, index) =>
            triggerSmallView ? (
              <motion.div
                initial={{ y: -13, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.38 }}
                className="team_wrapper"
                key={index}
              >
                <div
                  className={`team_icon-box label-btn ${
                    currentTeamId === team?.teamID ? "selected_team" : ""
                  } `}
                >
                  {team?.teamImage ? (
                    <img src={team?.teamImage} alt="Team" className="w-7 h-7 min-w-7 min-h-7 rounded"/>
                  ) : (
                    <Avatar
                      name={team?.teamName}
                      size={40}
                      fontSize={18}
                      className="para_color"
                    />
                  )}
                </div>
              </motion.div>
            ) : (
              <motion.div
                initial={{ y: -13, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.38 }}
              >
                <Accordion
                  key={index}
                  expanded={expanded === `panel${index}`}
                  onChange={handleChange(`panel${index}`)}
                  className="sidebar-team-accordion"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`panel${index}a-content`}
                    id={`panel${index}a-header`}
                    className="header"
                  >
                    <div
                      className={`team_icon-box label-btn ${
                        currentTeamId === team?.teamID ? "selected_team" : ""
                      } `}
                    >
                      {team?.teamImage ? (
                        <img src={team?.teamImage} alt="Team" className="w-5 h-5 min-w-5 min-h-5 rounded" />
                      ) : (
                        <Avatar
                          name={team?.teamName}
                          size={40}
                          fontSize={18}
                          className="para_color"
                        />
                      )}
                    </div>
                    <p>{team?.teamName}</p>
                  </AccordionSummary>
                  <AccordionDetails className="details">
                    <div className="team-members-box">
                      <p className="length">
                        {team?.teamMembers?.length} Team Members
                      </p>
                      <TeamMembersSide
                        headerTeamMembers={team?.teamMembers}
                        team={team}
                        handleClickedTeam={handleClickedTeam}
                      />
                    </div>
                    <div className="pharmacies">
                      <p className="length">
                        {team?.pharmacies?.length} Pharmacies
                      </p>
                      <div className="list">
                        {team?.pharmacies?.map((pharmacy) => (
                          <div
                            className={`pharmacy-box ${
                              currentPharmacyId === pharmacy.teamPharmacyID
                                ? "active"
                                : ""
                            }`}
                            onClick={() =>
                              handleTeamPharmacyClick(pharmacy, index)
                            }
                          >
                            <GoHomeFill />
                            <p>{pharmacy?.teamPharmacyName}</p>
                          </div>
                        ))}
                      </div>
                    </div>
                  </AccordionDetails>
                </Accordion>
              </motion.div>
            ),
          )
        ) : (
          <p className="no-results">No pharmacies found.</p>
        )}
      </div>
    </motion.div>
  );
}
